// @ts-check
// Traducción entre las tres representaciones (DEC-107).
//
// ## El IR es el eje, y por eso son cuatro piezas y no seis
//
// Las seis direcciones que pide el alumno (diagrama<->pseudocódigo,
// diagrama<->Python, pseudocódigo<->Python) NO son seis traductores. Cada
// representación se convierte a la IR y desde la IR, así que bastan cuatro
// adaptadores y las seis direcciones salen de componerlos:
//
//     pseudocódigo --\                    /-- pseudocódigo
//                     >--- IR-JSON ------<
//     Python -------/                     \-- Python
//     diagrama -----/  (ya existía)        \-- diagrama
//
// La alternativa -- seis traductores directos -- multiplicaría por tres el
// código y, peor, permitiría que "diagrama a Python" y "diagrama a
// pseudocódigo a Python" dieran resultados distintos. Con un eje único eso es
// imposible por construcción.
//
// El diagrama no necesita adaptador: su editor ya guarda el programa en IR
// (`diagram-editor/ir-serialization.js`), que fue la decisión de DEC-012.

import { irAPseudocodigo } from './ir-a-pseudocodigo.js';
import { irAPython } from './ir-a-python.js';
import { pseudocodigoAIr } from './pseudocodigo-a-ir.js';
import { pythonAIr } from './python-a-ir.js';
import { traduccionFallida } from './resultado.js';
import { BLOQUES, BLOQUES_INFO } from '../../utils/bloques.js';

export { irAPseudocodigo } from './ir-a-pseudocodigo.js';
export { irAPython } from './ir-a-python.js';
export { pseudocodigoAIr } from './pseudocodigo-a-ir.js';
export { pythonAIr } from './python-a-ir.js';

/**
 * Una representación ES un bloque del itinerario: mismo conjunto de valores y
 * mismo contrato con el backend. Se declara como alias del tipo compartido
 * (`utils/bloques.js`) y no como una unión propia, que era lo que permitía que
 * las dos se separasen sin que nada avisara.
 * @typedef {import('../../utils/bloques.js').Bloque} Representacion
 */

/**
 * Nombre de cada representación tal y como se le enseña al alumno. NO se
 * comparte con `utils/bloques.js` a propósito: es prosa que se incrusta en
 * frases ("No se puede convertir a pseudocódigo sin..."), en minúscula y con
 * "diagrama de flujo" completo, mientras que las etiquetas de navegación son
 * títulos. Vive aquí porque solo la usa esta feature.
 */
export const NOMBRE_REPRESENTACION = /** @type {Record<Representacion, string>} */ ({
  diagrama: 'diagrama de flujo',
  pseudocodigo: 'pseudocódigo',
  python: 'Python',
});

/**
 * Página donde vive cada editor, derivada del mapa compartido: era una tercera
 * copia del mismo bloque -> página, y la que abre `entrega.js` al llevar al
 * alumno de un editor a otro (un `href` desfasado aquí lo dejaría en una
 * página que no existe).
 * @type {Record<Representacion, string>}
 */
export const PAGINA_REPRESENTACION = /** @type {Record<Representacion, string>} */ (
  Object.fromEntries(BLOQUES_INFO.map((bloque) => [bloque.id, bloque.href]))
);

/**
 * Lleva cualquier representación a IR-JSON.
 *
 * El diagrama entra ya en IR (su editor la guarda así), de ahí que este caso
 * sea la identidad y no una conversión.
 * @param {Representacion} desde
 * @param {unknown} fuente Texto para pseudocódigo/Python; IR-JSON para diagrama.
 * @returns {import('./resultado.js').Traduccion<any>}
 */
export function aIr(desde, fuente) {
  switch (desde) {
    case 'diagrama':
      return typeof fuente === 'object' && fuente !== null
        ? { ok: true, valor: fuente }
        : traduccionFallida([{ descripcion: 'El diagrama está vacío.' }]);
    case 'pseudocodigo':
      return pseudocodigoAIr(String(fuente ?? ''));
    case 'python':
      return pythonAIr(String(fuente ?? ''));
    default:
      return traduccionFallida([{ descripcion: 'Representación desconocida.' }]);
  }
}

/**
 * Lleva IR-JSON a la representación indicada.
 * @param {Representacion} hacia
 * @param {any} ir
 * @returns {import('./resultado.js').Traduccion<any>}
 */
export function desdeIr(hacia, ir) {
  switch (hacia) {
    case 'diagrama':
      return { ok: true, valor: ir };
    case 'pseudocodigo':
      return irAPseudocodigo(ir);
    case 'python':
      return irAPython(ir);
    default:
      return traduccionFallida([{ descripcion: 'Representación desconocida.' }]);
  }
}

/**
 * Traduce de una representación a otra, pasando por el IR.
 *
 * Devuelve el mismo contrato que los adaptadores: o el resultado COMPLETO, o
 * la lista de motivos por los que no se ha podido. Nunca un resultado a
 * medias -- ver `resultado.js`.
 *
 * @param {{ desde: Representacion, hacia: Representacion, fuente: unknown }} peticion
 * @returns {import('./resultado.js').Traduccion<any>}
 */
export function traducir(peticion) {
  const { desde, hacia, fuente } = peticion;
  if (desde === hacia) {
    return traduccionFallida([{ descripcion: 'El origen y el destino son el mismo.' }]);
  }
  const ir = aIr(desde, fuente);
  if (!ir.ok) return ir;
  return desdeIr(hacia, ir.valor);
}

/**
 * Las dos representaciones a las que se puede traducir desde `desde`.
 * @param {Representacion} desde
 * @returns {Representacion[]}
 */
export function destinosDesde(desde) {
  return /** @type {Representacion[]} */ ([...BLOQUES]).filter((r) => r !== desde);
}
